import { Component, OnInit } from '@angular/core';
import { TreatmentInformationFacade } from '@modules/treatment-information/treatment-information.facade';
import { TreatmentInformationComponentPart } from '../models/treatment-information-component.enum';
import { TOOTH_EDITOR_PROVIDERS } from '@modules/tooth-editor/injection/providers';
import { TreatmentInformationBaseComponent } from '../base/treatment-information-base.component';
import { EmptyStringAsHyphenPipe } from '@modules/treatment-information/pipes/empty-string-as-hyphen.pipe';
import { ToothEditorService } from '@shared/services/tooth-editor.service';
import { FormControl, FormGroup } from '@angular/forms';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { IdName } from '@shared/models/id-name';
import { Maybe } from '@/types/globals';

@Component({
	selector: 'rx-treatment-information-v1',
	templateUrl: './treatment-information-v1.component.html',
	styleUrls: ['../base/treatment-information-base.component.scss'],
	providers: [TOOTH_EDITOR_PROVIDERS, EmptyStringAsHyphenPipe, TreatmentInformationFacade]
})
export class TreatmentInformationV1Component extends TreatmentInformationBaseComponent implements OnInit {
	componentPart = {
		treatmentInformation: TreatmentInformationComponentPart.TreatmentInformation,
		bridgeInformation: TreatmentInformationComponentPart.BridgeInformation
	};
	displayedColumns: string[] = ['Tooth No', 'Treatment', 'Specification', 'Material', 'Shade', 'Show details'];

	ponticDesignGroupControls = new FormGroup({});

	get ponticDesigns(): Maybe<IdName[]> {
		return this.facade.ponticDesigns;
	}

	constructor(
		protected readonly facade: TreatmentInformationFacade,
		protected readonly toothEditorService: ToothEditorService
	) {
		super(facade, toothEditorService);
	}

	managePonticDesignControl(bridge: Tooth[]): FormControl<any> {
		const toothWithPontic = bridge?.find(tooth => tooth.PonticDesignID && (tooth.PonticDesignID as number) > 0);
		const ponticControlName = this.getPonticDesignControlName(bridge) as any;

		if (!this.ponticDesignGroupControls.contains(ponticControlName)) {
			this.addOrRemoveControl(toothWithPontic, ponticControlName);
		}
		this.patchControlValue(toothWithPontic, ponticControlName);

		return this.ponticDesignGroupControls.get(ponticControlName) as FormControl<any>;
	}

	getShadeByTooth(incisal: string, body: string, gingival: string) {
		return this.facade.getShadeByTooth(this.isPrintContext, { incisal, body, gingival });
	}

	getFunctionForPonticRow(bridge: Tooth[]): (index: number, rowData: any) => boolean {
		return (index: number) => {
			const hasPontic = bridge?.some(tooth => tooth.PonticDesignID && (tooth.PonticDesignID as number) > 0);

			return hasPontic && index === bridge?.length - 1;
		};
	}

	private getPonticDesignControlName(bridge: Tooth[]): string | null {
		// @ts-expect-error - TODO: fix this type error after updating Tooth model
		const bridgeIndex = bridge?.find(tooth => tooth.BridgeIndex != null)?.BridgeIndex - 1;

		return bridgeIndex !== null ? `ponticControl-${bridgeIndex}` : null;
	}

	private addOrRemoveControl(toothWithPontic: Maybe<Tooth>, controlName: string) {
		if (toothWithPontic && !this.ponticDesignGroupControls.contains(controlName)) {
			this.ponticDesignGroupControls.addControl(controlName, new FormControl<IdName | null>(null));
		} else if (!toothWithPontic && this.ponticDesignGroupControls.contains(controlName)) {
			this.ponticDesignGroupControls.removeControl(controlName);
		}
	}

	private patchControlValue(toothWithPontic: Maybe<Tooth>, controlName: string) {
		const ponticDesign = this.ponticDesigns?.find(pontic => pontic.Id === toothWithPontic?.PonticDesignID);

		if (ponticDesign && this.ponticDesignGroupControls.contains(controlName)) {
			this.ponticDesignGroupControls.get(controlName)?.patchValue(ponticDesign);
		}
	}
}
